import React, { useCallback, useContext, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import styles from "./ExtraCostDateField.module.css";
import { completeShorthandYear, completeShorthandYearWhileTyping } from "src/utils/Date";

interface IQuantityFieldProps {
    accountingRowIndex: number;
}

const ExtraCostDateFieldBase = (props: IQuantityFieldProps) => {
    const { t } = useTranslation();
    const { invoiceStore, accountingUnitSearchStore } = useContext(RootStoreContext);

    const { accountingRowIndex } = props;

    const extraCostDateInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (
            accountingUnitSearchStore.triggerFocusNextAccountingExtraCostDateInput.isActive &&
            accountingUnitSearchStore.triggerFocusNextAccountingExtraCostDateInput.index === accountingRowIndex
        ) {
            extraCostDateInputRef.current?.focus();
            accountingUnitSearchStore.setTriggerFocusNextAccountingExtraCostDateInput(false, accountingRowIndex);
        }

        // This useEffect only listens to changes in accountingAccountSearchStore.triggerFocusNextInput
        // eslint-disable-next-line
    }, [accountingUnitSearchStore.triggerFocusNextAccountingExtraCostDateInput]);

    const onChangeExtraCostDate = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputExtraCostDate = completeShorthandYearWhileTyping(e.target.value);

            invoiceStore.updateAccountingWithExtraCostDate(inputExtraCostDate, accountingRowIndex);

            invoiceStore.currentInvoice?.validateAccountingsOnFieldLevel();
        },
        [invoiceStore, accountingRowIndex]
    );

    const completeExtraCostDateYear = useCallback(
        (enteredDate: string) => {
            const completedDate = completeShorthandYear(enteredDate);

            if (completedDate !== enteredDate) {
                invoiceStore.updateAccountingWithExtraCostDate(completedDate, accountingRowIndex);
            }
        },
        [invoiceStore, accountingRowIndex]
    );

    const onBlurExtraCostDate = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            completeExtraCostDateYear(e.target.value);
        },
        [completeExtraCostDateYear]
    );

    // the shorthand year has to be completed before Enter triggers an action on the invoice
    const onKeyDownExtraCostDate = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                completeExtraCostDateYear(e.currentTarget.value);
            }
        },
        [completeExtraCostDateYear]
    );

    return (
        <div className={invoiceStore.currentInvoice?.canEditExtraCostDateField(accountingRowIndex) ? "" : "invisible"}>
            <UI.Input
                ref={extraCostDateInputRef}
                label={
                    invoiceStore.currentInvoice?.canEditExtraCostDateField(accountingRowIndex)
                        ? `${t("screens.kredi_flow.accountings.extracostdate").toString()} *`
                        : undefined
                }
                type="date"
                autoComplete="off"
                min={"2000-01-01"}
                max={"2100-12-31"}
                value={invoiceStore.currentInvoice?.getAccountingExtraCostDate(accountingRowIndex) ?? ""}
                onChange={onChangeExtraCostDate}
                onBlur={onBlurExtraCostDate}
                onKeyDown={onKeyDownExtraCostDate}
            />
            {invoiceStore.currentInvoice?.accountingValidationErrors[accountingRowIndex] &&
                invoiceStore.currentInvoice?.accountingValidationErrors[accountingRowIndex]
                    .isExtraCostDateRangeValid === false && (
                    <span className={styles.DateError}>
                        {t("screens.kredi_flow.accountings.error.invalid_date_range")}
                    </span>
                )}
        </div>
    );
};

export const ExtraCostDateField = Sentry.withProfiler(observer(ExtraCostDateFieldBase));
